import React, { useMemo, useCallback } from 'react';
import { Plus, Mail, CheckCircle2, Coins, Activity, Workflow, ListFilter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isDevMode } from 'lib/featureFlags';
import PageHeader, { PrimaryButton, SecondaryButton } from 'components/ui/PageHeader';
import {
    Toolbar,
    ToolbarButton,
    FilterMenu,
    FilterChips,
    SortControl,
    ViewToggle,
    type FilterChip,
} from 'components/ui/toolbar';
import { SearchInput } from 'components/ui/search-input';
import { Field } from 'components/ui/field';
import { SelectField } from "components/ui/select-field";

/* ══════════════════════════════════════════════════════════════════════════
   Campaigns page header.

   The filter bar here used to be ~250 lines of bespoke markup: a hand-built
   search input with its own debounce timer and clear button, a hand-built
   status dropdown trigger, a hand-built "Filters" toggle with its own count
   badge, native `<SelectField>` elements for sort, and an advanced panel of five
   more native selects. Leads, Audiences and Templates each had their own
   variant of the same thing.

   It is now composed from the shared toolbar parts, which also fixes two
   usability problems the old bar had:

   • Applied filters were invisible. The state lived inside collapsed
     dropdowns, so a list filtered down to nothing looked like a list with no
     data. Active filters now appear as removable chips, each one click to undo.
   • The result count was buried in the advanced panel, visible only while it
     was expanded. It now sits on the toolbar whenever a filter is applied.

   Every prop, callback and filter key is unchanged.
   ══════════════════════════════════════════════════════════════════════════ */

const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'progress', label: 'Progress' },
    { value: 'pc', label: 'Partially Completed' },
    { value: 'comp', label: 'Completed' },
    { value: 'terminated', label: 'Terminated' },
];

const SORT_OPTIONS = [
    { value: 'updatedAt', label: 'Last Modified' },
    { value: 'createdAt', label: 'Created Date' },
    { value: 'name', label: 'Name' },
    { value: 'performance', label: 'Performance' },
    { value: 'status', label: 'Status' },
];

interface AdvancedFilter {
    key: 'status' | 'aiGenerated' | 'performance' | 'dateRange';
    label: string;
    options: Array<{ value: string; label: string }>;
}

const ADVANCED_FILTERS: AdvancedFilter[] = [
    {
        key: 'status',
        label: 'Status',
        options: [
            { value: 'all', label: 'All Status' },
            { value: 'draft', label: 'Draft' },
            { value: 'scheduled', label: 'Scheduled' },
            { value: 'active', label: 'Active' },
            { value: 'paused', label: 'Paused' },
            { value: 'completed', label: 'Completed' },
            { value: 'archived', label: 'Archived' },
        ],
    },
    {
        key: 'aiGenerated',
        label: 'AI Generated',
        options: [
            { value: 'all', label: 'All Campaigns' },
            { value: 'true', label: 'AI Generated' },
            { value: 'false', label: 'Manual' },
        ],
    },
    {
        key: 'performance',
        label: 'Performance',
        options: [
            { value: 'all', label: 'All Performance' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' },
        ],
    },
    {
        key: 'dateRange',
        label: 'Date Range',
        options: [
            { value: 'all', label: 'All Time' },
            { value: 'week', label: 'Last Week' },
            { value: 'month', label: 'Last Month' },
            { value: 'quarter', label: 'Last Quarter' },
            { value: 'year', label: 'Last Year' },
        ],
    },
];

const CampaignsHeader = ({
    campaignData,
    campaigns = [],
    filteredCampaignsCount = 0,
    selectedCampaignsCount = 0,
    searchTerm = '',
    onSearchChange,
    statusFilter = 'all',
    onStatusFilterChange,
    typeFilter = 'all',
    onTypeFilterChange,
    aiGeneratedFilter = 'all',
    onAiGeneratedFilterChange,
    performanceFilter = 'all',
    onPerformanceFilterChange,
    dateRangeFilter = 'all',
    onDateRangeFilterChange,
    sortBy = 'updatedAt',
    onSortByChange,
    sortOrder = 'desc',
    onSortOrderChange,
    showFilters = false,
    onToggleFilters,
    onClearFilters,
    onCreateCampaign,
    debounceMs = 300,
    toggleCampaignFilter,
    campaignFilter,
    isAllStatusSelected = true,
    onAllStatusToggle,
    viewMode = 'list',
    onViewModeChange,
    isLoading = false,
}) => {
    const navigate = useNavigate();

    const uniqueTypes = useMemo(
        () => [...new Set(campaigns.map((campaign) => campaign.type))].filter(Boolean).sort(),
        [campaigns]
    );

    const campaignStats = useMemo(() => {
        const items = campaignData?.items || campaigns;
        const totalCampaigns = campaignData?.total || items.length;
        const activeCampaigns = items.filter(
            (c) =>
                c.c_status?.toLowerCase() === 'active' || c.c_status?.toLowerCase() === 'progress'
        ).length;
        /* These two were `aiGeneratedCount: 0, totalRevenue: 0` — constants, so
           half the strip could never show anything but a zero. They were not
           merely unwired: ICampaign carries id, name, temp_id, temp_name,
           s_date, e_date, c_status, c_grp, email_used and credits_used, so
           there is no AI flag and no revenue figure to read. A tile that cannot
           be filled is worse than one fewer tile.

           These two can be filled, from the fields the endpoint does return. */
        const completedCampaigns = items.filter((c) => {
            const st = String(c.c_status ?? '').toLowerCase();
            return st === 'comp' || st === 'completed' || st === 'pc';
        }).length;
        const creditsUsed = items.reduce((sum, c) => sum + (Number(c.credits_used) || 0), 0);

        return { totalCampaigns, activeCampaigns, completedCampaigns, creditsUsed };
    }, [campaigns, campaignData]);

    /* ── Status multi-select, mapped onto the existing boolean filter keys ── */
    const selectedStatuses = useMemo(
        () => STATUS_OPTIONS.filter((o) => campaignFilter?.[o.value]).map((o) => o.value),
        [campaignFilter]
    );

    const handleStatusChange = useCallback(
        (next) => {
            if (next.length === 0) {
                onAllStatusToggle?.(true);
                return;
            }
            STATUS_OPTIONS.forEach((option) => {
                const shouldBeOn = next.includes(option.value);
                const isOn = !!campaignFilter?.[option.value];
                if (shouldBeOn !== isOn) toggleCampaignFilter?.(option.value, shouldBeOn);
            });
        },
        [campaignFilter, toggleCampaignFilter, onAllStatusToggle]
    );

    /* ── Applied-filter chips ── */
    const advancedValues = {
        status: statusFilter,
        type: typeFilter,
        aiGenerated: aiGeneratedFilter,
        performance: performanceFilter,
        dateRange: dateRangeFilter,
    };

    const advancedSetters = {
        status: onStatusFilterChange,
        type: onTypeFilterChange,
        aiGenerated: onAiGeneratedFilterChange,
        performance: onPerformanceFilterChange,
        dateRange: onDateRangeFilterChange,
    };

    const chips: FilterChip[] = useMemo(() => {
        const out: FilterChip[] = [];

        if (!isAllStatusSelected) {
            selectedStatuses.forEach((value) => {
                const option = STATUS_OPTIONS.find((o) => o.value === value);
                out.push({
                    id: `status-${value}`,
                    field: 'Status',
                    label: option?.label ?? value,
                    onRemove: () => toggleCampaignFilter?.(value, false),
                });
            });
        }

        if (isDevMode()) {
            ADVANCED_FILTERS.forEach((filter) => {
                const value = advancedValues[filter.key];
                if (!value || value === 'all') return;
                const option = filter.options.find((o) => o.value === value);
                out.push({
                    id: `adv-${filter.key}`,
                    field: filter.label,
                    label: option?.label ?? value,
                    onRemove: () => advancedSetters[filter.key]?.('all'),
                });
            });

            if (typeFilter && typeFilter !== 'all') {
                out.push({
                    id: 'adv-type',
                    field: 'Type',
                    label: typeFilter,
                    onRemove: () => onTypeFilterChange?.('all'),
                });
            }
        }

        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isAllStatusSelected,
        selectedStatuses,
        statusFilter,
        typeFilter,
        aiGeneratedFilter,
        performanceFilter,
        dateRangeFilter,
    ]);

    const hasActiveFilters = chips.length > 0 || !!searchTerm;

    const handleClearAll = useCallback(() => {
        onSearchChange?.('');
        onAllStatusToggle?.(true);
        onClearFilters?.();
    }, [onSearchChange, onAllStatusToggle, onClearFilters]);

    const kpis = [
        {
            icon: Mail,
            value: campaignStats.totalCampaigns,
            label: 'Total Campaigns',
            tone: 'brand' as const,
        },
        {
            icon: Activity,
            value: campaignStats.activeCampaigns,
            label: 'Active Campaigns',
            tone: 'success' as const,
        },
        {
            icon: CheckCircle2,
            value: campaignStats.completedCampaigns,
            label: 'Completed',
            tone: 'violet' as const,
        },
        {
            icon: Coins,
            value: campaignStats.creditsUsed.toLocaleString(),
            label: 'Credits Used',
            tone: 'warning' as const,
        },
    ];

    return (
        <>
            <PageHeader
                icon={<Mail className="w-5 h-5" />}
                tone="brand"
                title="Campaigns"
                /* The count lives in the Total Campaigns tile directly below, so
                   "2 total" here said it twice, inches apart. The subtitle says
                   what the page is for; the selection count still surfaces here
                   because nothing else states it until you scroll to the list. */
                subtitle={
                    selectedCampaignsCount > 0
                        ? `${selectedCampaignsCount} selected`
                        : 'Create, schedule and track your email campaigns'
                }
                actions={
                    <>
                        <SecondaryButton
                            icon={<Workflow className="w-4 h-4" />}
                            onClick={() => navigate('/campaigns/flow')}
                        >
                            Campaign Flow
                        </SecondaryButton>
                        <PrimaryButton
                            icon={<Plus className="w-4 h-4" />}
                            onClick={() => (onCreateCampaign ? onCreateCampaign() : navigate('/campaigns/new'))}
                        >
                            Create Campaign
                        </PrimaryButton>
                    </>
                }
                kpis={kpis}
                isLoading={isLoading}
            />

            {/* No bottom margin: the page is a `Page`, whose gap-5 already
                separates it from the list. `mb-5` on top of that was 40px. */}
            <Toolbar
                search={
                    <SearchInput
                        value={searchTerm}
                        onChange={(value) => onSearchChange?.(value)}
                        onDebouncedChange={(value) => onSearchChange?.(value)}
                        debounceMs={debounceMs}
                        placeholder="Search by name, description, template or segment…"
                        aria-label="Search campaigns"
                    />
                }
                filters={
                    <>
                        <FilterMenu
                            label="Status"
                            icon={<ListFilter className="w-3.5 h-3.5" />}
                            options={STATUS_OPTIONS}
                            value={isAllStatusSelected ? [] : selectedStatuses}
                            onChange={handleStatusChange}
                            allLabel="All Campaigns"
                        />
                        {isDevMode() && (
                            <ToolbarButton
                                onClick={onToggleFilters}
                                active={showFilters}
                                aria-expanded={showFilters}
                                aria-controls="campaign-advanced-filters"
                            >
                                More filters
                            </ToolbarButton>
                        )}
                    </>
                }
                /* The list/grid switch belongs here, with the other view
                   controls. It used to sit in a second strip below this card
                   alongside "Select all N" — so the page showed two competing
                   control bars, and this one was left looking empty because in
                   production the sort control is dev-only. One bar now: search,
                   filters, then how you want it displayed. */
                actions={
                    <>
                        {isDevMode() && (
                            <SortControl
                                options={SORT_OPTIONS}
                                value={sortBy}
                                onChange={(value) => onSortByChange?.(value)}
                                direction={sortOrder === 'asc' ? 'asc' : 'desc'}
                                onDirectionChange={(dir) => onSortOrderChange?.(dir)}
                            />
                        )}
                        {onViewModeChange && (
                            <ViewToggle value={viewMode} onChange={onViewModeChange} />
                        )}
                    </>
                }
                chips={<FilterChips chips={chips} onClearAll={handleClearAll} />}
                summary={
                    hasActiveFilters
                        ? `${filteredCampaignsCount} of ${campaignStats.totalCampaigns} shown`
                        : null
                }
                expanded={
                    isDevMode() && showFilters ? (
                        <div id="campaign-advanced-filters" className="space-y-3.5">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                                {ADVANCED_FILTERS.map((filter) => (
                                    <Field key={filter.key} label={filter.label}>
                                        {(control) => (
                                            <SelectField
                                                {...control}
                                                value={advancedValues[filter.key]}
                                                onChange={(e) => advancedSetters[filter.key]?.(e.target.value)}
                                                className="input-field text-ui font-medium"
                                            >
                                                {filter.options.map((o) => (
                                                    <option key={o.value} value={o.value}>
                                                        {o.label}
                                                    </option>
                                                ))}
                                            </SelectField>
                                        )}
                                    </Field>
                                ))}

                                <Field label="Type">
                                    {(control) => (
                                        <SelectField
                                            {...control}
                                            value={typeFilter}
                                            onChange={(e) => onTypeFilterChange?.(e.target.value)}
                                            className="input-field text-ui font-medium"
                                        >
                                            <option value="all">All Types</option>
                                            {uniqueTypes.map((t) => (
                                                <option key={t} value={t}>
                                                    {t.charAt(0).toUpperCase() + t.slice(1)}
                                                </option>
                                            ))}
                                        </SelectField>
                                    )}
                                </Field>
                            </div>

                            <div className="flex justify-between items-center pt-3 border-t border-line">
                                <span className="text-meta font-medium text-content-muted tabular-nums">
                                    {filteredCampaignsCount} of {campaigns.length} campaigns shown
                                </span>
                                <button
                                    type="button"
                                    onClick={onClearFilters}
                                    className="text-meta font-semibold text-brand hover:text-brand-hover px-2 py-1 rounded-lg hover:bg-brand-subtle transition-colors focus-ring"
                                >
                                    Clear all filters
                                </button>
                            </div>
                        </div>
                    ) : null
                }
            />
        </>
    );
};

export default CampaignsHeader;
